
import { UserProfile, LeaderboardEntry, Banner, SocialLinks, Mantra, Ebook, SatsangAudio, SatsangVideo, NidhiScan, AUDIO_CATEGORIES, EBOOK_CATEGORIES, ContactInfo, BrandingConfig, ReminderConfig, AkhandRoom, AppNotification, DailyShloka, SidebarMenuItem, DEFAULT_SIDEBAR_MENU, EkadashiDate, EKADASHI_DATES, EkadashiReminderSettings, BlogPost, BlogCategory, BlogComment } from '../types';
import { DEFAULT_MANTRAS, DEFAULT_AUDIOS } from '../constants';
import { getAllFromStore, saveToStore, deleteFromStore, getOneFromStore, saveMultipleToStore } from './db';
import { enqueue } from './offlineSync';
import { API_URL } from './apiConfig';

const SESSION_KEY = 'ram_naam_session_user';
const BANNERS_KEY = 'ram_naam_banners';
const SOCIALS_KEY = 'ram_naam_socials';
const MANTRAS_KEY = 'ram_naam_mantras';
const CONTACT_KEY = 'ram_naam_contact';
const BRANDING_KEY = 'ram_naam_branding';
const REMINDER_KEY = 'ram_naam_reminder_config';
const NOTIFICATIONS_KEY = 'ram_naam_notifications';
const SHLOKAS_KEY = 'ram_naam_shlokas';

const EBOOKS_STORE = 'ebooks';
const AUDIOS_STORE = 'audios';
const VIDEOS_STORE = 'videos';
const USERS_STORE = 'users';
const NIDHI_SCANS_STORE = 'nidhi_scans';
const SHLOKAS_STORE = 'shlokas';
const BLOGS_STORE = 'blogs';
const BLOG_CATEGORIES_STORE = 'blog_categories';

interface UserRecord {
  username: string; 
  passwordHash: string;
  profile: UserProfile;
}

const normalizeProfile = (profile: UserProfile): UserProfile => {
  if (!profile.mantra_counts) {
    profile.mantra_counts = { ram: profile.total_count || 0 };
  }
  return {
    ...profile,
    displayName: profile.displayName || profile.username,
    bio: profile.bio || 'Sacred soul on a divine journey.',
    avatar: profile.avatar || '',
    email: profile.email || '',
    dob: profile.dob || '',
    mobile: profile.mobile || '',
    sect: profile.sect || '',
    likhit_counts: profile.likhit_counts || {},
    smaran_counts: profile.smaran_counts || {},
    vani_counts: profile.vani_counts || {},
    nidhi_counts: profile.nidhi_counts || {},
    saved_audios: profile.saved_audios || [],
    saved_blogs: profile.saved_blogs || [],
    prasadam_gallery: profile.prasadam_gallery || [],
    ebook_progress: profile.ebook_progress || [],
    legacy_config: profile.legacy_config || {
      heirs: [],
      inactivityTriggerDays: 365,
      isFinalized: false
    }
  };
};

export const loginUser = async (username: string, password: string): Promise<UserProfile> => {
  const allUsers = await getAllFromStore<UserRecord>(USERS_STORE);
  const userRecord = allUsers.find(
    u => u.username.toLowerCase() === username.trim().toLowerCase() || 
         (u.profile.email && u.profile.email.toLowerCase() === username.trim().toLowerCase())
  );

  if (!userRecord || userRecord.passwordHash !== password) {
    throw new Error("Invalid credentials.");
  }
  const normalizedProfile = normalizeProfile(userRecord.profile);
  localStorage.setItem(SESSION_KEY, userRecord.username);
  // Set session_token for API authentication (use username as token for local users)
  localStorage.setItem('session_token', `local_${userRecord.username}_${Date.now()}`);
  cacheUser(normalizedProfile); // Cache for instant loading
  return normalizedProfile;
};

export const loginUserByMobile = async (mobile: string, password: string): Promise<UserProfile> => {
  const allRecords = await getAllFromStore<UserRecord>(USERS_STORE);
  const userRecord = allRecords.find(r => r.profile.mobile === mobile);
  if (!userRecord || userRecord.passwordHash !== password) {
    throw new Error("Invalid mobile number or password.");
  }
  const normalizedProfile = normalizeProfile(userRecord.profile);
  localStorage.setItem(SESSION_KEY, userRecord.username);
  // Set session_token for API authentication
  localStorage.setItem('session_token', `local_${userRecord.username}_${Date.now()}`);
  cacheUser(normalizedProfile); // Cache for instant loading
  return normalizedProfile;
};

export const registerUser = async (
  username: string, 
  password: string,
  details?: { realName?: string, guruPradattNaam?: string, dob?: string, email?: string, phone?: string, displayName?: string }
): Promise<UserProfile> => {
  const cleanUsername = username.trim().toLowerCase();
  const allUsers = await getAllFromStore<UserRecord>(USERS_STORE);
  const existing = allUsers.find(u => u.username.toLowerCase() === cleanUsername);
  
  if (existing) throw new Error("Username already taken. Please choose another.");

  if (details?.email) {
    const emailExists = allUsers.find(u => u.profile.email?.toLowerCase() === details.email!.toLowerCase());
    if (emailExists) throw new Error("यह Email पहले से registered है।");
  }

  if (details?.phone) {
    const phoneExists = allUsers.find(u => u.profile.mobile === details.phone);
    if (phoneExists) throw new Error("यह Phone number पहले से registered है।");
  }

  const profile: UserProfile = {
    id: `u_${Date.now()}`,
    username: cleanUsername,
    realName: details?.realName,
    guruPradattNaam: details?.guruPradattNaam,
    displayName: details?.displayName || details?.guruPradattNaam || details?.realName || cleanUsername,
    dob: details?.dob,
    email: details?.email,
    mobile: details?.phone,
    total_count: 0,
    mantra_counts: {},
    likhit_counts: {},
    smaran_counts: {},
    vani_counts: {},
    nidhi_counts: {},
    streak_days: 0,
    joined_date: new Date().toISOString(),
    saved_audios: [],
    prasadam_gallery: [],
    isGuest: false,
    legacy_config: {
      heirs: [],
      inactivityTriggerDays: 365,
      isFinalized: false
    }
  };

  await saveToStore<UserRecord>(USERS_STORE, {
    username: cleanUsername,
    passwordHash: password,
    profile
  });

  const normalizedProfile = normalizeProfile(profile);
  localStorage.setItem(SESSION_KEY, cleanUsername);
  // Set session_token for API authentication
  localStorage.setItem('session_token', `local_${cleanUsername}_${Date.now()}`);
  cacheUser(normalizedProfile); // Cache for instant loading
  return normalizedProfile;
};

export const registerUserByMobile = async (
  mobile: string, 
  password: string,
  details?: { realName?: string, guruPradattNaam?: string, dob?: string, email?: string }
): Promise<UserProfile> => {
  const all = await getAllFromStore<UserRecord>(USERS_STORE);
  if (all.some(u => u.profile.mobile === mobile)) throw new Error("Mobile number already registered.");

  let username = `user_${mobile.slice(-4)}_${Math.floor(Math.random() * 1000)}`;
  while (all.some(u => u.username === username)) {
    username = `user_${mobile.slice(-4)}_${Math.floor(Math.random() * 1000)}`;
  }

  const profile: UserProfile = {
    id: `u_${Date.now()}`,
    username,
    mobile,
    realName: details?.realName,
    guruPradattNaam: details?.guruPradattNaam,
    displayName: details?.guruPradattNaam || details?.realName || username,
    dob: details?.dob,
    email: details?.email,
    total_count: 0,
    mantra_counts: {},
    likhit_counts: {},
    smaran_counts: {},
    vani_counts: {},
    nidhi_counts: {},
    streak_days: 0,
    joined_date: new Date().toISOString(),
    saved_audios: [],
    prasadam_gallery: [],
    isGuest: false,
    legacy_config: {
      heirs: [],
      inactivityTriggerDays: 365,
      isFinalized: false
    }
  };

  await saveToStore<UserRecord>(USERS_STORE, {
    username,
    passwordHash: password,
    profile
  });

  const normalizedProfile = normalizeProfile(profile);
  localStorage.setItem(SESSION_KEY, username);
  // Set session_token for API authentication
  localStorage.setItem('session_token', `local_${username}_${Date.now()}`);
  cacheUser(normalizedProfile); // Cache for instant loading
  return normalizedProfile;
};

export const syncGoogleUser = async (email: string, name: string, avatar: string, backendCounts?: any): Promise<UserProfile> => {
  const cleanEmail = email.toLowerCase();
  const username = cleanEmail.split('@')[0];
  const allUsers = await getAllFromStore<UserRecord>(USERS_STORE);
  const existing = allUsers.find(
    u => u.username.toLowerCase() === username.toLowerCase() || 
         u.username.toLowerCase() === cleanEmail ||
         (u.profile.email && u.profile.email.toLowerCase() === cleanEmail)
  );

  if (existing) {
    // Merge backend counts (take the higher value)
    if (backendCounts) {
      const countFields = ['total_count', 'streak_days'] as const;
      const dictFields = ['mantra_counts', 'likhit_counts', 'smaran_counts', 'vani_counts', 'nidhi_counts'] as const;
      for (const f of countFields) {
        if (backendCounts[f] !== undefined) {
          (existing.profile as any)[f] = Math.max((existing.profile as any)[f] || 0, backendCounts[f] || 0);
        }
      }
      for (const f of dictFields) {
        const local = (existing.profile as any)[f] || {};
        const remote = backendCounts[f] || {};
        const merged: Record<string, number> = { ...local };
        for (const k of Object.keys(remote)) {
          merged[k] = Math.max(merged[k] || 0, remote[k] || 0);
        }
        (existing.profile as any)[f] = merged;
      }
      await saveToStore<UserRecord>(USERS_STORE, existing);
    }
    const normalizedProfile = normalizeProfile(existing.profile);
    localStorage.setItem(SESSION_KEY, existing.username);
    cacheUser(normalizedProfile);
    return normalizedProfile;
  }

  const profile: UserProfile = {
    id: `u_${Date.now()}`,
    username,
    email: cleanEmail,
    avatar,
    displayName: name,
    total_count: backendCounts?.total_count || 0,
    mantra_counts: backendCounts?.mantra_counts || {},
    likhit_counts: backendCounts?.likhit_counts || {},
    smaran_counts: backendCounts?.smaran_counts || {},
    vani_counts: backendCounts?.vani_counts || {},
    nidhi_counts: backendCounts?.nidhi_counts || {},
    streak_days: backendCounts?.streak_days || 0,
    joined_date: new Date().toISOString(),
    saved_audios: [],
    prasadam_gallery: [],
    isGuest: false,
    legacy_config: {
      heirs: [],
      inactivityTriggerDays: 365,
      isFinalized: false
    }
  };

  // Use username (not full email) consistently for storage key
  await saveToStore<UserRecord>(USERS_STORE, {
    username: username, // Changed from cleanEmail to username for consistency
    passwordHash: 'google_oauth_bypass',
    profile
  });

  const normalizedProfile = normalizeProfile(profile);
  localStorage.setItem(SESSION_KEY, username);
  cacheUser(normalizedProfile); // Cache for instant loading
  return normalizedProfile;
};

// Sync a password/OTP-logged-in user to IndexedDB + localStorage for session persistence
export const syncPasswordUser = async (email: string, name: string, backendProfile: UserProfile): Promise<UserProfile> => {
  const cleanEmail = email.toLowerCase().trim();
  const username = cleanEmail.split('@')[0] || cleanEmail;
  
  // Check if user already exists in IndexedDB
  const allUsers = await getAllFromStore<UserRecord>(USERS_STORE);
  const existing = allUsers.find(
    u => u.profile.email?.toLowerCase() === cleanEmail || u.username.toLowerCase() === username
  );
  
  if (existing) {
    // Merge: take HIGHER count values between local and backend
    const countFields = ['total_count', 'streak_days'] as const;
    const dictFields = ['mantra_counts', 'likhit_counts', 'smaran_counts', 'vani_counts', 'nidhi_counts'] as const;
    const merged = { ...existing.profile, ...backendProfile, id: backendProfile.id || existing.profile.id };
    for (const f of countFields) {
      (merged as any)[f] = Math.max((existing.profile as any)[f] || 0, (backendProfile as any)[f] || 0);
    }
    for (const f of dictFields) {
      const local = (existing.profile as any)[f] || {};
      const remote = (backendProfile as any)[f] || {};
      const m: Record<string, number> = { ...local };
      for (const k of Object.keys(remote)) {
        m[k] = Math.max(m[k] || 0, remote[k] || 0);
      }
      (merged as any)[f] = m;
    }
    existing.profile = merged;
    await saveToStore(USERS_STORE, existing);
    const normalizedProfile = normalizeProfile(existing.profile);
    localStorage.setItem(SESSION_KEY, existing.username);
    cacheUser(normalizedProfile);
    return normalizedProfile;
  }
  
  // Create new user record
  const profile: UserProfile = {
    id: backendProfile.id || `u_${Date.now()}`,
    username: username,
    displayName: name || username,
    email: cleanEmail,
    ...backendProfile
  };
  
  const newUser: UserRecord = { username, passwordHash: '', profile };
  await saveToStore(USERS_STORE, newUser);
  
  const normalizedProfile = normalizeProfile(profile);
  localStorage.setItem(SESSION_KEY, username);
  cacheUser(normalizedProfile);
  return normalizedProfile;
};


export const updatePassword = async (newPassword: string): Promise<void> => {
  const username = localStorage.getItem(SESSION_KEY);
  if (!username) throw new Error("Session expired.");
  const userRecord = await getOneFromStore<UserRecord>(USERS_STORE, username);
  if (!userRecord) throw new Error("User not found.");
  userRecord.passwordHash = newPassword;
  await saveToStore(USERS_STORE, userRecord);
};

const USER_CACHE_KEY = 'ram_naam_user_cache';

// Get cached user profile from localStorage (instant)
export const getCachedUser = (): UserProfile | null => {
  try {
    const cached = localStorage.getItem(USER_CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch {
    // Invalid cache, ignore
  }
  return null;
};

// Cache user profile to localStorage
export const cacheUser = (profile: UserProfile | null) => {
  if (profile) {
    localStorage.setItem(USER_CACHE_KEY, JSON.stringify(profile));
  } else {
    localStorage.removeItem(USER_CACHE_KEY);
  }
};

export const getCurrentUser = async (): Promise<UserProfile | null> => {
  const username = localStorage.getItem(SESSION_KEY);
  if (!username) {
    cacheUser(null);
    return null;
  }
  
  // First get from local IndexedDB
  const userRecord = await getOneFromStore<UserRecord>(USERS_STORE, username);
  let profile = userRecord ? normalizeProfile(userRecord.profile) : null;
  
  // Try to fetch latest from backend (for logged-in users)
  const sessionToken = localStorage.getItem('session_token');
  if (profile && !profile.isGuest && sessionToken) {
    try {
          
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      headers['Authorization'] = `Bearer ${sessionToken}`;
      
      const response = await fetch(`${API_URL}/api/user/profile`, {
        method: 'GET',
        headers,
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          // Merge backend data with local profile (use higher count)
          const backendCounts = data.user.mantra_counts || {};
          const localCounts = profile.mantra_counts || {};
          
          const mergedCounts: Record<string, number> = { ...localCounts };
          for (const key of Object.keys(backendCounts)) {
            mergedCounts[key] = Math.max(backendCounts[key] || 0, localCounts[key] || 0);
          }
          
          profile = {
            ...profile,
            mantra_counts: mergedCounts,
            total_count: Math.max(data.user.total_count || 0, profile.total_count || 0),
            displayName: data.user.displayName || data.user.name || profile.displayName,
            guruPradattNaam: data.user.guruPradattNaam || profile.guruPradattNaam,
            sect: data.user.sect || profile.sect,
            mobile: data.user.mobile || profile.mobile,
            language: data.user.language || profile.language
          };
          
          // Update local storage with merged data
          if (userRecord) {
            userRecord.profile = profile;
            await saveToStore(USERS_STORE, userRecord);
          }
        }
      } else if (response.status === 401) {
        // Session expired/invalid - clear ALL stale tokens and force re-login
        console.warn('Session expired/invalid. Clearing stale session.');
        localStorage.removeItem('session_token');
        localStorage.removeItem(SESSION_KEY);
        cacheUser(null);
        return null;
      }
    } catch {
      // Network error - use local data (offline mode)
    }
  }
  
  cacheUser(profile);
  return profile;
};

export const logoutUser = () => {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(USER_CACHE_KEY);
  localStorage.removeItem('session_token');
};

export const saveUserData = async (profile: UserProfile): Promise<void> => {
  // Save to local IndexedDB
  const userRecord = await getOneFromStore<UserRecord>(USERS_STORE, profile.username);
  if (userRecord) {
    userRecord.profile = profile;
    await saveToStore(USERS_STORE, userRecord);
    cacheUser(profile); // Update cache
  }
  
  // Sync to backend MongoDB (for logged-in users)
  const sessionToken = localStorage.getItem('session_token');
  if (!profile.isGuest && sessionToken) {
      const syncUrl = `${API_URL}/api/user/profile`;
    const syncHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${sessionToken}`,
    };
    const syncBody = JSON.stringify({
      mantra_counts: profile.mantra_counts,
      total_count: profile.total_count,
      likhit_counts: profile.likhit_counts,
      smaran_counts: profile.smaran_counts,
      vani_counts: profile.vani_counts,
      nidhi_counts: profile.nidhi_counts,
      streak_days: profile.streak_days,
      displayName: profile.displayName,
      guruPradattNaam: profile.guruPradattNaam,
      sect: profile.sect,
      mobile: profile.mobile,
      language: profile.language
    });

    try {
      await fetch(syncUrl, { method: 'PUT', headers: syncHeaders, body: syncBody });
    } catch {
      // Offline — queue for later sync
      enqueue({ url: syncUrl, method: 'PUT', headers: syncHeaders, body: syncBody, type: 'jaap_sync' });
    }
  }
};

export const getAllUsers = async (): Promise<UserProfile[]> => {
  const all = await getAllFromStore<UserRecord>(USERS_STORE);
  return all.map(r => normalizeProfile(r.profile));
};

export const getLeaderboard = async (): Promise<LeaderboardEntry[]> => {
  const users = await getAllUsers();
  return users
    .sort((a, b) => b.total_count - a.total_count)
    .slice(0, 50)
    .map((u, i) => ({
      rank: i + 1,
      username: u.displayName || u.username,
      count: u.total_count,
      badge: i === 0 ? 'Gold' : (i === 1 ? 'Silver' : (i === 2 ? 'Bronze' : undefined))
    }));
};

export const getBranding = (): BrandingConfig => {
  const saved = localStorage.getItem(BRANDING_KEY);
  if (saved) return JSON.parse(saved);
  return {
    appName: 'गोलोक दिव्य नाम कोष',
    subTitle: 'Divine Ledger',
    logoUrl: '/logo.png',
    logoHeight: 80
  };
};

// Fetch branding from server (async version)
export const fetchBrandingFromServer = async (): Promise<BrandingConfig> => {
  try {
    const res = await fetch(`${API_URL}/api/branding`);
    const data = await res.json();
    if (data.success && data.branding) {
      // Ensure logoUrl always has a fallback
      if (!data.branding.logoUrl) {
        data.branding.logoUrl = '/logo.png';
      }
      // Cache in localStorage for offline/fast access
      localStorage.setItem(BRANDING_KEY, JSON.stringify(data.branding));
      return data.branding;
    }
  } catch (err) {
    console.warn('Failed to fetch branding from server, using local cache');
  }
  return getBranding();
};

export const saveBranding = async (config: BrandingConfig): Promise<boolean> => {
  // Save to localStorage (immediate)
  localStorage.setItem(BRANDING_KEY, JSON.stringify(config));
  
  // Save to server (persistent)
  const token = localStorage.getItem('session_token');
  try {
    const res = await fetch(`${API_URL}/api/branding`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      credentials: 'include',
      body: JSON.stringify({ branding: config }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      const errMsg = `Status: ${res.status} - ${data.detail || 'Unknown error'}`;
      console.error('Branding save failed:', errMsg);
      alert(`Error: ${errMsg}`);
      return false;
    }
    return true;
  } catch (err: any) {
    console.error('Branding save network error:', err);
    alert(`Network Error: ${err.message}`);
    return false;
  }
};

export const getBanners = (): Banner[] => {
  const saved = localStorage.getItem(BANNERS_KEY);
  return saved ? JSON.parse(saved) : [];
};

export const fetchBannersFromServer = async (): Promise<Banner[]> => {
  try {
    const res = await fetch(`${API_URL}/api/banners`);
    const data = await res.json();
    if (data.success && data.banners) {
      localStorage.setItem(BANNERS_KEY, JSON.stringify(data.banners));
      return data.banners;
    }
  } catch (err) {
    console.warn('Failed to fetch banners from server');
  }
  return getBanners();
};

export const saveBanners = async (banners: Banner[]) => {
  localStorage.setItem(BANNERS_KEY, JSON.stringify(banners));
  const token = localStorage.getItem('session_token');
  try {
    await fetch(`${API_URL}/api/banners`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      credentials: 'include',
      body: JSON.stringify({ banners }),
    });
  } catch (err) { console.error('Failed to save banners to server:', err); }
};

export const getSocialLinks = (): SocialLinks => {
  const saved = localStorage.getItem(SOCIALS_KEY);
  return saved ? JSON.parse(saved) : {
    facebook: 'https://facebook.com/golokpremras',
    instagram: 'https://instagram.com/golokpremras',
    youtube: 'https://youtube.com/@golokpremras',
    twitter: 'https://twitter.com/golokpremras',
    whatsapp: 'https://chat.whatsapp.com/KiBikPLXAu0Gw61MdJaY3f?mode=gi_t',
    telegram: 'https://t.me/golokpremras'
  };
};

export const fetchSocialLinksFromServer = async (): Promise<SocialLinks> => {
  try {
    const res = await fetch(`${API_URL}/api/social-links`);
    const data = await res.json();
    if (data.success && data.links && Object.keys(data.links).length > 0) {
      localStorage.setItem(SOCIALS_KEY, JSON.stringify(data.links));
      return data.links;
    }
  } catch (err) {
    console.warn('Failed to fetch social links from server');
  }
  return getSocialLinks();
};

export const saveSocialLinks = async (links: SocialLinks) => {
  localStorage.setItem(SOCIALS_KEY, JSON.stringify(links));
  const token = localStorage.getItem('session_token');
  try {
    await fetch(`${API_URL}/api/social-links`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      credentials: 'include',
      body: JSON.stringify({ links }),
    });
  } catch (err) { console.error('Failed to save social links to server:', err); }
};

export const getReminderConfig = (): ReminderConfig => {
  const saved = localStorage.getItem(REMINDER_KEY);
  return saved ? JSON.parse(saved) : {
    enabled: false,
    intervalMinutes: 15,
    mantraText: 'श्री राम जय राम जय जय राम',
    volume: 1.0,
    useCustomAudio: false,
    customAudioData: undefined
  };
};

export const saveReminderConfig = (config: ReminderConfig) => {
  localStorage.setItem(REMINDER_KEY, JSON.stringify(config));
};

export const getContactInfo = (): ContactInfo => {
  const saved = localStorage.getItem(CONTACT_KEY);
  if (saved) return JSON.parse(saved);
  
  return {
    phone: '7771040506, 8269442434', 
    email: 'golokstores@gmail.com, golokpremras@gmail.com', 
    address: '195, Mandakini Colony, JK Hospital Road, Kolar Road, Bhopal - 462042', 
    whatsapp: '918269442434', 
    supportHours: '9 AM - 6 PM IST',
    website: 'www.golokstore.com'
  };
};

export const fetchContactInfoFromServer = async (): Promise<ContactInfo> => {
  try {
    const res = await fetch(`${API_URL}/api/contact-info`);
    const data = await res.json();
    if (data.success && data.contact && Object.keys(data.contact).length > 0) {
      localStorage.setItem(CONTACT_KEY, JSON.stringify(data.contact));
      return data.contact;
    }
  } catch (err) {
    console.warn('Failed to fetch contact info from server');
  }
  return getContactInfo();
};

export const saveContactInfo = async (info: ContactInfo) => {
  localStorage.setItem(CONTACT_KEY, JSON.stringify(info));
  const token = localStorage.getItem('session_token');
  try {
    await fetch(`${API_URL}/api/contact-info`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      credentials: 'include',
      body: JSON.stringify({ contact: info }),
    });
  } catch (err) { console.error('Failed to save contact info to server:', err); }
};


export const getEbooks = async (): Promise<Ebook[]> => {
  try {
    // Try to fetch from backend API first
    const response = await fetch(`${API_URL}/api/ebooks`);
    if (response.ok) {
      const data = await response.json();
      return (data.ebooks || []).map((ebook: any) => ({
        ...ebook,
        // Note: pdfData is NOT included in list response for performance
        // Use getEbookById() to get full ebook with pdfData when opening viewer
        active: ebook.active !== false && ebook.isPublished !== false,
        fileType: ebook.fileType || (ebook.epubUrl ? 'epub' : 'pdf')
      }));
    }
  } catch (error) {
    console.warn('Failed to fetch ebooks from API, falling back to local:', error);
  }
  
  // Fallback to local IndexedDB
  const ebooks = await getAllFromStore<Ebook>(EBOOKS_STORE);
  return ebooks.map(ebook => ({
    ...ebook,
    fileType: ebook.fileType || (ebook.epubUrl ? 'epub' : 'pdf')
  }));
};

// Get single ebook with full data (including pdfData) for viewer
export const getEbookById = async (id: string): Promise<Ebook | null> => {
  try {
    const response = await fetch(`${API_URL}/api/ebooks/${id}`);
    if (response.ok) {
      const data = await response.json();
      const ebook = data.ebook;
      return {
        ...ebook,
        active: ebook.active !== false && ebook.isPublished !== false,
        fileType: ebook.fileType || (ebook.epubUrl ? 'epub' : 'pdf'),
        // Default to true if not explicitly set to false
        allowDownload: ebook.allowDownload !== false,
        allowScreenshot: ebook.allowScreenshot !== false
      };
    }
  } catch (error) {
    console.warn('Failed to fetch ebook by ID:', error);
  }
  
  // Fallback to IndexedDB
  const ebooks = await getAllFromStore<Ebook>(EBOOKS_STORE);
  return ebooks.find(e => e.id === id) || null;
};

export const saveEbook = async (ebook: Ebook, forceCreate: boolean = false): Promise<void> => {
  try {
    // Save to backend API
    const token = localStorage.getItem('session_token');
    
    // For new uploads, forceCreate=true means always POST
    // For updates (toggle buttons), check if ID looks like existing book
    let isUpdate = false;
    if (!forceCreate && ebook.id) {
      // Try to check if book exists by fetching it
      try {
        const checkResponse = await fetch(`${API_URL}/api/ebooks/${ebook.id}`);
        isUpdate = checkResponse.ok;
      } catch {
        isUpdate = false;
      }
    }
    
    const url = isUpdate ? `${API_URL}/api/ebooks/${ebook.id}` : `${API_URL}/api/ebooks`;
    const method = isUpdate ? 'PUT' : 'POST';
    
    console.log(`Saving ebook: ${method} ${url}`);
    
    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: JSON.stringify(ebook)
    });
    
    if (response.ok) {
      console.log('Ebook saved to backend successfully');
      return;
    }
    
    // If not authorized or error, save locally
    console.warn('Backend save failed, saving locally');
  } catch (error) {
    console.warn('Failed to save ebook to API, saving locally:', error);
  }
  
  // Fallback to local IndexedDB
  await saveToStore(EBOOKS_STORE, ebook);
};

export const deleteEbookById = async (id: string): Promise<void> => {
  try {
    const token = localStorage.getItem('session_token');
    const response = await fetch(`${API_URL}/api/ebooks/${id}`, {
      method: 'DELETE',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      }
    });
    
    if (response.ok) {
      console.log('Ebook deleted from backend');
      return;
    }
  } catch (error) {
    console.warn('Failed to delete from API:', error);
  }
  
  await deleteFromStore(EBOOKS_STORE, id);
};

export const getAudios = async (): Promise<SatsangAudio[]> => {
  const audios = await getAllFromStore<SatsangAudio>(AUDIOS_STORE);
  if (audios.length === 0 && DEFAULT_AUDIOS.length > 0) {
    await saveMultipleToStore(AUDIOS_STORE, DEFAULT_AUDIOS);
    return DEFAULT_AUDIOS;
  }
  return audios;
};

export const saveAudio = async (audio: SatsangAudio): Promise<void> => {
  await saveToStore(AUDIOS_STORE, audio);
};

export const deleteAudioById = async (id: string): Promise<void> => {
  await deleteFromStore(AUDIOS_STORE, id);
};

export const getAudioCategories = (): string[] => {
  const saved = localStorage.getItem('audio_categories');
  return saved ? JSON.parse(saved) : [...AUDIO_CATEGORIES];
};

export const saveAudioCategories = (cats: string[]) => {
  localStorage.setItem('audio_categories', JSON.stringify(cats));
};

export const getEbookCategories = (): string[] => {
  const saved = localStorage.getItem('ebook_categories');
  return saved ? JSON.parse(saved) : [...EBOOK_CATEGORIES];
};

export const saveEbookCategories = (cats: string[]) => {
  localStorage.setItem('ebook_categories', JSON.stringify(cats));
};

export const getVideos = async (): Promise<SatsangVideo[]> => getAllFromStore<SatsangVideo>(VIDEOS_STORE);

export const saveVideo = async (video: SatsangVideo): Promise<void> => saveToStore(VIDEOS_STORE, video);

export const deleteVideoById = async (id: string): Promise<void> => deleteFromStore(VIDEOS_STORE, id);

export const getMantras = (): Mantra[] => {
  const saved = localStorage.getItem(MANTRAS_KEY);
  if (!saved) return DEFAULT_MANTRAS;
  
  const savedMantras: Mantra[] = JSON.parse(saved);
  
  // Merge with DEFAULT_MANTRAS to ensure patterns are up-to-date
  // If a saved mantra exists in defaults, use default patterns if saved has none/empty
  return savedMantras.map(savedMantra => {
    const defaultMantra = DEFAULT_MANTRAS.find(d => d.id === savedMantra.id);
    if (defaultMantra && (!savedMantra.patterns || savedMantra.patterns.length === 0)) {
      return { ...savedMantra, patterns: defaultMantra.patterns };
    }
    return savedMantra;
  });
};

export const saveMantras = async (mantras: Mantra[]): Promise<void> => {
  // Save to localStorage for immediate use
  localStorage.setItem(MANTRAS_KEY, JSON.stringify(mantras));
  
  // Sync with backend - add new mantras that don't exist in backend
  try {
    const existingMantras = await fetch(`${API_URL}/api/mantras`).then(r => r.json());
    const existingIds = new Set(existingMantras.map((m: Mantra) => m.id));
    
    for (const mantra of mantras) {
      if (!existingIds.has(mantra.id)) {
        // Add new mantra to backend (this also creates Sangh room)
        await fetch(`${API_URL}/api/mantras`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(mantra)
        });
      }
    }
  } catch (error) {
    console.log('Backend mantra sync skipped:', error);
  }
};

export const getAkhandRooms = (): AkhandRoom[] => {
  return [
    {
      id: 'room_1',
      name: 'Global Ram Naam Room',
      hindiName: 'वैश्विक राम नाम संकीर्तन',
      mantraId: 'ram',
      participantCount: 0,
      collectiveTotal: 0,
      description: 'Join thousands of devotees across the world in continuous Ram Naam chanting.',
      creatorId: 'admin' 
    },
    {
      id: 'room_2',
      name: 'Holy Radha Sangha',
      hindiName: 'दिव्य राधा संकीर्तन',
      mantraId: 'radha',
      participantCount: 0,
      collectiveTotal: 0,
      description: 'Connect with the divine grace of Shri Radha Rani in this constant stream of devotion.',
      creatorId: 'admin'
    },
    {
      id: 'room_3',
      name: 'Maha Mantra Akhand Jaap',
      hindiName: 'अखंड महामंत्र जाप',
      mantraId: 'mahamantra',
      participantCount: 0,
      collectiveTotal: 0,
      description: 'The ultimate Hare Krishna Maha Mantra sangha for purification of the soul.',
      creatorId: 'admin'
    }
  ];
};

// --- Notification Services ---

const DEFAULT_NOTIFICATIONS: AppNotification[] = [
  {
    id: 'notif_welcome',
    title: 'Welcome to Divine Bank',
    message: 'Start your spiritual journey today by depositing 108 names. May Lord Ram bless you.',
    type: 'system',
    timestamp: new Date().toISOString(),
    read: false
  },
  {
    id: 'notif_feature_ai',
    title: 'New: AI Sant Vani',
    message: 'Seek guidance from our AI Oracle trained on the wisdom of great saints. Ask any spiritual question.',
    type: 'info',
    timestamp: new Date(Date.now() - 3600000).toISOString(),
    read: false,
    link: 'AI_SANT_VANI'
  },
  {
    id: 'notif_ekadashi',
    title: 'Upcoming Ekadashi',
    message: 'The holy day of Ekadashi is approaching. Prepare for special Akhand Jaap sessions.',
    type: 'milestone',
    timestamp: new Date(Date.now() - 86400000).toISOString(),
    read: true
  }
];

export const getNotifications = (): AppNotification[] => {
  const saved = localStorage.getItem(NOTIFICATIONS_KEY);
  if (!saved) {
    localStorage.setItem(NOTIFICATIONS_KEY, JSON.stringify(DEFAULT_NOTIFICATIONS));
    return DEFAULT_NOTIFICATIONS;
  }
  return JSON.parse(saved);
};

export const markNotificationRead = (id: string) => {
  const notifications = getNotifications();
  const updated = notifications.map(n => n.id === id ? { ...n, read: true } : n);
  localStorage.setItem(NOTIFICATIONS_KEY, JSON.stringify(updated));
};

export const markAllNotificationsRead = () => {
  const notifications = getNotifications();
  const updated = notifications.map(n => ({ ...n, read: true }));
  localStorage.setItem(NOTIFICATIONS_KEY, JSON.stringify(updated));
};

export const addNotification = (notification: Omit<AppNotification, 'id' | 'timestamp' | 'read'>) => {
  const notifications = getNotifications();
  const newNotification: AppNotification = {
    ...notification,
    id: `notif_${Date.now()}`,
    timestamp: new Date().toISOString(),
    read: false
  };
  localStorage.setItem(NOTIFICATIONS_KEY, JSON.stringify([newNotification, ...notifications]));
};

// --- Nidhi Scan Services (Naam Lekhan Scanner) ---

export const getNidhiScans = async (): Promise<NidhiScan[]> => {
  return getAllFromStore<NidhiScan>(NIDHI_SCANS_STORE);
};

export const getNidhiScansByUser = async (userId: string): Promise<NidhiScan[]> => {
  const allScans = await getAllFromStore<NidhiScan>(NIDHI_SCANS_STORE);
  return allScans.filter(scan => scan.userId === userId);
};

export const saveNidhiScan = async (scan: NidhiScan): Promise<void> => {
  await saveToStore(NIDHI_SCANS_STORE, scan);
};

export const deleteNidhiScan = async (id: string): Promise<void> => {
  await deleteFromStore(NIDHI_SCANS_STORE, id);
};


// --- Shloka/Quote of the Day Services ---

// Default Bhagavad Gita Shlokas with Hindi meanings
const DEFAULT_SHLOKAS: DailyShloka[] = [
  {
    id: 'shloka_1',
    type: 'shloka',
    sanskritText: 'कर्मण्येवाधिकारस्ते मा फलेषु कदाचन।\nमा कर्मफलहेतुर्भूर्मा ते सङ्गोऽस्त्वकर्मणि॥',
    hindiTransliteration: 'कर्मण्य एवाधिकारस्ते मा फलेषु कदाचन\nमा कर्म-फल-हेतुर्भूः मा ते सङ्गोऽस्त्वकर्मणि',
    hindiMeaning: 'तुम्हें केवल कर्म करने का अधिकार है, उसके फलों में कभी नहीं। तुम कर्मफल के हेतु मत बनो और न ही तुम्हारी अकर्म में आसक्ति हो।',
    englishMeaning: 'You have the right to work only, but never to its fruits. Let not the fruits of action be your motive, nor let your attachment be to inaction.',
    source: 'Bhagavad Gita 2.47',
    chapter: 2,
    verse: 47,
    tags: ['karma', 'action', 'duty'],
    active: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'shloka_2',
    type: 'shloka',
    sanskritText: 'योगस्थः कुरु कर्माणि सङ्गं त्यक्त्वा धनञ्जय।\nसिद्ध्यसिद्ध्योः समो भूत्वा समत्वं योग उच्यते॥',
    hindiTransliteration: 'योग-स्थः कुरु कर्माणि सङ्गं त्यक्त्वा धनञ्जय\nसिद्ध्य-असिद्ध्योः समो भूत्वा समत्वं योग उच्यते',
    hindiMeaning: 'हे धनञ्जय! आसक्ति को त्यागकर, सफलता और असफलता में समभाव रखते हुए योग में स्थित होकर कर्म करो। इसी समत्व को योग कहते हैं।',
    englishMeaning: 'Perform your duty equipoised, O Arjuna, abandoning all attachment to success or failure. Such equanimity is called yoga.',
    source: 'Bhagavad Gita 2.48',
    chapter: 2,
    verse: 48,
    tags: ['yoga', 'equanimity', 'balance'],
    active: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'shloka_3',
    type: 'shloka',
    sanskritText: 'यदा यदा हि धर्मस्य ग्लानिर्भवति भारत।\nअभ्युत्थानमधर्मस्य तदात्मानं सृजाम्यहम्॥',
    hindiTransliteration: 'यदा यदा हि धर्मस्य ग्लानिः भवति भारत\nअभ्युत्थानम् अधर्मस्य तदा आत्मानं सृजामि अहम्',
    hindiMeaning: 'हे भारत! जब-जब धर्म की हानि और अधर्म की वृद्धि होती है, तब-तब मैं स्वयं को प्रकट करता हूँ।',
    englishMeaning: 'Whenever there is a decline in righteousness and an increase in unrighteousness, O Arjuna, at that time I manifest myself.',
    source: 'Bhagavad Gita 4.7',
    chapter: 4,
    verse: 7,
    tags: ['dharma', 'avatar', 'divine'],
    active: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'shloka_4',
    type: 'shloka',
    sanskritText: 'परित्राणाय साधूनां विनाशाय च दुष्कृताम्।\nधर्मसंस्थापनार्थाय सम्भवामि युगे युगे॥',
    hindiTransliteration: 'परित्राणाय साधूनां विनाशाय च दुष्कृताम्\nधर्म-संस्थापन-अर्थाय सम्भवामि युगे युगे',
    hindiMeaning: 'साधुओं की रक्षा करने के लिए, दुष्टों का विनाश करने के लिए और धर्म की स्थापना करने के लिए मैं युग-युग में प्रकट होता हूँ।',
    englishMeaning: 'To protect the righteous, to annihilate the wicked, and to reestablish dharma, I appear millennium after millennium.',
    source: 'Bhagavad Gita 4.8',
    chapter: 4,
    verse: 8,
    tags: ['protection', 'dharma', 'avatar'],
    active: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'shloka_5',
    type: 'shloka',
    sanskritText: 'श्रद्धावान् लभते ज्ञानं तत्परः संयतेन्द्रियः।\nज्ञानं लब्ध्वा परां शान्तिमचिरेणाधिगच्छति॥',
    hindiTransliteration: 'श्रद्धावान् लभते ज्ञानं तत्-परः संयत-इन्द्रियः\nज्ञानं लब्ध्वा परां शान्तिम् अचिरेण अधिगच्छति',
    hindiMeaning: 'श्रद्धावान, तत्पर और इन्द्रियों को वश में करने वाला मनुष्य ज्ञान प्राप्त करता है और ज्ञान प्राप्त करके शीघ्र ही परम शान्ति को प्राप्त होता है।',
    englishMeaning: 'A faithful person who is dedicated and has control over the senses attains knowledge. Having obtained knowledge, one quickly attains supreme peace.',
    source: 'Bhagavad Gita 4.39',
    chapter: 4,
    verse: 39,
    tags: ['faith', 'knowledge', 'peace'],
    active: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'shloka_6',
    type: 'shloka',
    sanskritText: 'मन्मना भव मद्भक्तो मद्याजी मां नमस्कुरु।\nमामेवैष्यसि सत्यं ते प्रतिजाने प्रियोऽसि मे॥',
    hindiTransliteration: 'मन्-मना भव मद्-भक्तो मद्-याजी मां नमस्कुरु\nमाम् एव एष्यसि सत्यं ते प्रतिजाने प्रियः असि मे',
    hindiMeaning: 'मुझमें मन वाला हो, मेरा भक्त बन, मेरा पूजन कर और मुझे प्रणाम कर। इस प्रकार मुझसे युक्त होकर तू निश्चय ही मुझे प्राप्त होगा, यह सत्य प्रतिज्ञा मैं तुझसे करता हूँ क्योंकि तू मुझे प्रिय है।',
    englishMeaning: 'Fix your mind on Me, be devoted to Me, worship Me, and bow to Me. Thus, uniting yourself with Me and dedicating yourself to Me, you shall certainly come to Me.',
    source: 'Bhagavad Gita 18.65',
    chapter: 18,
    verse: 65,
    tags: ['devotion', 'bhakti', 'surrender'],
    active: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'shloka_7',
    type: 'shloka',
    sanskritText: 'सर्वधर्मान्परित्यज्य मामेकं शरणं व्रज।\nअहं त्वां सर्वपापेभ्यो मोक्षयिष्यामि मा शुचः॥',
    hindiTransliteration: 'सर्व-धर्मान् परित्यज्य माम् एकं शरणं व्रज\nअहं त्वां सर्व-पापेभ्यो मोक्षयिष्यामि मा शुचः',
    hindiMeaning: 'सभी धर्मों को त्यागकर केवल मेरी शरण में आ जाओ। मैं तुम्हें सभी पापों से मुक्त कर दूँगा, शोक मत करो।',
    englishMeaning: 'Abandon all varieties of dharma and simply surrender unto Me alone. I shall liberate you from all sins; do not grieve.',
    source: 'Bhagavad Gita 18.66',
    chapter: 18,
    verse: 66,
    tags: ['surrender', 'liberation', 'grace'],
    active: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'shloka_8',
    type: 'shloka',
    sanskritText: 'ध्यायतो विषयान्पुंसः सङ्गस्तेषूपजायते।\nसङ्गात्सञ्जायते कामः कामात्क्रोधोऽभिजायते॥',
    hindiTransliteration: 'ध्यायतो विषयान् पुंसः सङ्गः तेषु उपजायते\nसङ्गात् सञ्जायते कामः कामात् क्रोधः अभिजायते',
    hindiMeaning: 'विषयों का चिन्तन करने वाले पुरुष की उनमें आसक्ति हो जाती है, आसक्ति से कामना उत्पन्न होती है और कामना से क्रोध उत्पन्न होता है।',
    englishMeaning: 'While contemplating the objects of the senses, a person develops attachment for them. From attachment desire is born, and from desire anger arises.',
    source: 'Bhagavad Gita 2.62',
    chapter: 2,
    verse: 62,
    tags: ['attachment', 'desire', 'anger'],
    active: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'shloka_9',
    type: 'shloka',
    sanskritText: 'क्रोधाद्भवति सम्मोहः सम्मोहात्स्मृतिविभ्रमः।\nस्मृतिभ्रंशाद् बुद्धिनाशो बुद्धिनाशात्प्रणश्यति॥',
    hindiTransliteration: 'क्रोधात् भवति सम्मोहः सम्मोहात् स्मृति-विभ्रमः\nस्मृति-भ्रंशात् बुद्धि-नाशो बुद्धि-नाशात् प्रणश्यति',
    hindiMeaning: 'क्रोध से मोह उत्पन्न होता है, मोह से स्मृति भ्रष्ट हो जाती है, स्मृति भ्रष्ट होने से बुद्धि नष्ट हो जाती है और बुद्धि नष्ट होने से मनुष्य का पतन हो जाता है।',
    englishMeaning: 'From anger arises delusion, from delusion bewilderment of memory. When memory is bewildered, intelligence is destroyed, and when intelligence is destroyed, one falls down.',
    source: 'Bhagavad Gita 2.63',
    chapter: 2,
    verse: 63,
    tags: ['anger', 'delusion', 'destruction'],
    active: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'shloka_10',
    type: 'shloka',
    sanskritText: 'नैनं छिन्दन्ति शस्त्राणि नैनं दहति पावकः।\nन चैनं क्लेदयन्त्यापो न शोषयति मारुतः॥',
    hindiTransliteration: 'न एनं छिन्दन्ति शस्त्राणि न एनं दहति पावकः\nन च एनं क्लेदयन्ति आपो न शोषयति मारुतः',
    hindiMeaning: 'इस आत्मा को शस्त्र काट नहीं सकते, अग्नि जला नहीं सकती, जल गीला नहीं कर सकता और वायु सुखा नहीं सकती।',
    englishMeaning: 'The soul can never be cut by any weapon, nor can it be burned by fire, nor moistened by water, nor withered by wind.',
    source: 'Bhagavad Gita 2.23',
    chapter: 2,
    verse: 23,
    tags: ['soul', 'immortal', 'eternal'],
    active: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'shloka_11',
    type: 'shloka',
    sanskritText: 'वासांसि जीर्णानि यथा विहाय नवानि गृह्णाति नरोऽपराणि।\nतथा शरीराणि विहाय जीर्णान्यन्यानि संयाति नवानि देही॥',
    hindiTransliteration: 'वासांसि जीर्णानि यथा विहाय नवानि गृह्णाति नरो अपराणि\nतथा शरीराणि विहाय जीर्णानि अन्यानि संयाति नवानि देही',
    hindiMeaning: 'जैसे मनुष्य पुराने वस्त्रों को त्यागकर नए वस्त्र धारण करता है, वैसे ही आत्मा पुराने शरीरों को त्यागकर नए शरीरों में प्रवेश करती है।',
    englishMeaning: 'As a person puts on new garments, giving up old ones, similarly, the soul accepts new material bodies, giving up the old and useless ones.',
    source: 'Bhagavad Gita 2.22',
    chapter: 2,
    verse: 22,
    tags: ['soul', 'rebirth', 'body'],
    active: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'shloka_12',
    type: 'shloka',
    sanskritText: 'अनन्याश्चिन्तयन्तो मां ये जनाः पर्युपासते।\nतेषां नित्याभियुक्तानां योगक्षेमं वहाम्यहम्॥',
    hindiTransliteration: 'अनन्याः चिन्तयन्तो मां ये जनाः पर्युपासते\nतेषां नित्य-अभियुक्तानां योग-क्षेमं वहामि अहम्',
    hindiMeaning: 'जो भक्त अनन्य भाव से मेरा चिन्तन करते हुए मेरी उपासना करते हैं, उन नित्य मुझमें लगे हुए भक्तों के योगक्षेम का वहन मैं स्वयं करता हूँ।',
    englishMeaning: 'But those who worship Me with exclusive devotion, meditating on My transcendental form - to them I carry what they lack and preserve what they have.',
    source: 'Bhagavad Gita 9.22',
    chapter: 9,
    verse: 22,
    tags: ['devotion', 'protection', 'grace'],
    active: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'shloka_13',
    type: 'shloka',
    sanskritText: 'पत्रं पुष्पं फलं तोयं यो मे भक्त्या प्रयच्छति।\nतदहं भक्त्युपहृतमश्नामि प्रयतात्मनः॥',
    hindiTransliteration: 'पत्रं पुष्पं फलं तोयं यो मे भक्त्या प्रयच्छति\nतत् अहं भक्ति-उपहृतम् अश्नामि प्रयत-आत्मनः',
    hindiMeaning: 'जो कोई भक्त मुझे प्रेम से एक पत्ता, एक फूल, एक फल या जल भी अर्पण करता है, मैं उस शुद्ध हृदय वाले भक्त की प्रेमपूर्ण भेंट को स्वीकार करता हूँ।',
    englishMeaning: 'If one offers Me with love and devotion a leaf, a flower, fruit or water, I will accept it from the one of purified consciousness.',
    source: 'Bhagavad Gita 9.26',
    chapter: 9,
    verse: 26,
    tags: ['bhakti', 'offering', 'love'],
    active: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'shloka_14',
    type: 'shloka',
    sanskritText: 'यत्करोषि यदश्नासि यज्जुहोषि ददासि यत्।\nयत्तपस्यसि कौन्तेय तत्कुरुष्व मदर्पणम्॥',
    hindiTransliteration: 'यत् करोषि यत् अश्नासि यत् जुहोषि ददासि यत्\nयत् तपस्यसि कौन्तेय तत् कुरुष्व मत्-अर्पणम्',
    hindiMeaning: 'हे कौन्तेय! तुम जो कुछ करते हो, जो खाते हो, जो हवन करते हो, जो दान देते हो और जो तप करते हो, वह सब मुझे अर्पण करो।',
    englishMeaning: 'Whatever you do, whatever you eat, whatever you offer or give away, and whatever austerities you perform - do that as an offering to Me.',
    source: 'Bhagavad Gita 9.27',
    chapter: 9,
    verse: 27,
    tags: ['offering', 'dedication', 'karma'],
    active: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'shloka_15',
    type: 'shloka',
    sanskritText: 'उद्धरेदात्मनात्मानं नात्मानमवसादयेत्।\nआत्मैव ह्यात्मनो बन्धुरात्मैव रिपुरात्मनः॥',
    hindiTransliteration: 'उद्धरेत् आत्मना आत्मानं न आत्मानम् अवसादयेत्\nआत्मा एव हि आत्मनो बन्धुः आत्मा एव रिपुः आत्मनः',
    hindiMeaning: 'मनुष्य को अपने आप से अपना उद्धार करना चाहिए और अपने आप को गिरने नहीं देना चाहिए, क्योंकि आत्मा ही आत्मा का मित्र है और आत्मा ही आत्मा का शत्रु है।',
    englishMeaning: 'One must elevate, not degrade, oneself by one\'s own mind. The mind can be the friend or the enemy of the self.',
    source: 'Bhagavad Gita 6.5',
    chapter: 6,
    verse: 5,
    tags: ['self', 'mind', 'upliftment'],
    active: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'shloka_16',
    type: 'shloka',
    sanskritText: 'ईश्वरः सर्वभूतानां हृद्देशेऽर्जुन तिष्ठति।\nभ्रामयन्सर्वभूतानि यन्त्रारूढानि मायया॥',
    hindiTransliteration: 'ईश्वरः सर्व-भूतानां हृत्-देशे अर्जुन तिष्ठति\nभ्रामयन् सर्व-भूतानि यन्त्र-आरूढानि मायया',
    hindiMeaning: 'हे अर्जुन! ईश्वर सभी प्राणियों के हृदय में निवास करता है और अपनी माया से सभी प्राणियों को यंत्र पर आरूढ़ करके घुमाता रहता है।',
    englishMeaning: 'The Supreme Lord is situated in everyone\'s heart, O Arjuna, and is directing the wanderings of all living entities, who are seated as on a machine, made of the material energy.',
    source: 'Bhagavad Gita 18.61',
    chapter: 18,
    verse: 61,
    tags: ['ishwar', 'heart', 'maya'],
    active: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'shloka_17',
    type: 'shloka',
    sanskritText: 'न हि ज्ञानेन सदृशं पवित्रमिह विद्यते।\nतत्स्वयं योगसंसिद्धः कालेनात्मनि विन्दति॥',
    hindiTransliteration: 'न हि ज्ञानेन सदृशं पवित्रम् इह विद्यते\nतत् स्वयं योग-संसिद्धः कालेन आत्मनि विन्दति',
    hindiMeaning: 'इस संसार में ज्ञान के समान पवित्र करने वाला कुछ भी नहीं है। योग से सिद्ध हुआ मनुष्य समय आने पर स्वयं अपने आप में उसे प्राप्त कर लेता है।',
    englishMeaning: 'In this world, there is nothing so sublime and pure as transcendental knowledge. One who has become perfect in yoga finds this within the self in due course of time.',
    source: 'Bhagavad Gita 4.38',
    chapter: 4,
    verse: 38,
    tags: ['knowledge', 'purity', 'yoga'],
    active: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'shloka_18',
    type: 'shloka',
    sanskritText: 'समोऽहं सर्वभूतेषु न मे द्वेष्योऽस्ति न प्रियः।\nये भजन्ति तु मां भक्त्या मयि ते तेषु चाप्यहम्॥',
    hindiTransliteration: 'समो अहं सर्व-भूतेषु न मे द्वेष्यो अस्ति न प्रियः\nये भजन्ति तु मां भक्त्या मयि ते तेषु च अपि अहम्',
    hindiMeaning: 'मैं सभी प्राणियों में समान हूँ, न कोई मुझे अप्रिय है और न कोई प्रिय। परन्तु जो मुझे प्रेम से भजते हैं, वे मुझमें हैं और मैं उनमें हूँ।',
    englishMeaning: 'I am equal to all beings; I have no one hateful or dear to me. But those who worship Me with devotion are in Me, and I am also in them.',
    source: 'Bhagavad Gita 9.29',
    chapter: 9,
    verse: 29,
    tags: ['equality', 'devotion', 'bhakti'],
    active: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'shloka_19',
    type: 'shloka',
    sanskritText: 'अपि चेत्सुदुराचारो भजते मामनन्यभाक्।\nसाधुरेव स मन्तव्यः सम्यग्व्यवसितो हि सः॥',
    hindiTransliteration: 'अपि चेत् सु-दुराचारो भजते माम् अनन्य-भाक्\nसाधुः एव स मन्तव्यः सम्यक् व्यवसितो हि सः',
    hindiMeaning: 'यदि कोई अत्यन्त दुराचारी भी अनन्य भाव से मेरा भक्त होकर मुझे भजता है, तो उसे साधु ही मानना चाहिए क्योंकि उसने निश्चय ठीक किया है।',
    englishMeaning: 'Even if the most sinful person worships Me with exclusive devotion, they are to be considered saintly because they have rightly resolved.',
    source: 'Bhagavad Gita 9.30',
    chapter: 9,
    verse: 30,
    tags: ['redemption', 'devotion', 'grace'],
    active: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'shloka_20',
    type: 'shloka',
    sanskritText: 'यो मां पश्यति सर्वत्र सर्वं च मयि पश्यति।\nतस्याहं न प्रणश्यामि स च मे न प्रणश्यति॥',
    hindiTransliteration: 'यो मां पश्यति सर्वत्र सर्वं च मयि पश्यति\nतस्य अहं न प्रणश्यामि स च मे न प्रणश्यति',
    hindiMeaning: 'जो मुझे सब में और सब को मुझमें देखता है, उसके लिए मैं कभी अदृश्य नहीं होता और वह मेरे लिए कभी अदृश्य नहीं होता।',
    englishMeaning: 'For one who sees Me everywhere and sees everything in Me, I am never lost, nor is he ever lost to Me.',
    source: 'Bhagavad Gita 6.30',
    chapter: 6,
    verse: 30,
    tags: ['vision', 'omnipresence', 'unity'],
    active: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'quote_1',
    type: 'quote',
    sanskritText: 'जहाँ राम वहाँ काम नाहीं, जहाँ काम वहाँ राम नाहीं।\nदोनों कबहुँ न मिलैं, राम काम मन माहीं॥',
    hindiTransliteration: 'जहाँ राम वहाँ काम नाहीं, जहाँ काम वहाँ राम नाहीं',
    hindiMeaning: 'जहाँ भगवान् राम का निवास होता है वहाँ कामना नहीं रहती, और जहाँ कामना होती है वहाँ राम नहीं होते। ये दोनों मन में एक साथ नहीं रह सकते।',
    englishMeaning: 'Where there is Ram, there is no desire; where there is desire, there is no Ram. Both can never coexist in the mind.',
    source: 'Sant Kabir',
    author: 'Sant Kabir Das',
    tags: ['kabir', 'desire', 'devotion'],
    active: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'quote_2',
    type: 'quote',
    sanskritText: 'सियाराममय सब जग जानी।\nकरउँ प्रनाम जोरि जुग पानी॥',
    hindiTransliteration: 'सियाराममय सब जग जानी\nकरउँ प्रनाम जोरि जुग पानी',
    hindiMeaning: 'सारे संसार को सीताराम मय जानकर दोनों हाथ जोड़कर मैं सबको प्रणाम करता हूँ।',
    englishMeaning: 'Knowing the whole world to be pervaded by Sita and Ram, I bow with folded hands to all.',
    source: 'Ramcharitmanas',
    author: 'Goswami Tulsidas',
    tags: ['tulsidas', 'ram', 'devotion'],
    active: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'quote_3',
    type: 'quote',
    sanskritText: 'राम नाम मनिदीप धरु जीह देहरी द्वार।\nतुलसी भीतर बाहिरौ जौ चाहसि उजिआर॥',
    hindiTransliteration: 'राम नाम मनिदीप धरु जीह देहरी द्वार\nतुलसी भीतर बाहिरौ जौ चाहसि उजिआर',
    hindiMeaning: 'तुलसीदास जी कहते हैं कि यदि तुम भीतर और बाहर दोनों ओर प्रकाश चाहते हो तो जिह्वा रूपी द्वार की देहरी पर राम नाम रूपी मणिदीप रख दो।',
    englishMeaning: 'Tulsidas says, if you want light both inside and outside, place the jewel-lamp of Ram Naam on the threshold of your tongue.',
    source: 'Vinay Patrika',
    author: 'Goswami Tulsidas',
    tags: ['tulsidas', 'ram naam', 'light'],
    active: true,
    createdAt: new Date().toISOString()
  }
];

export const getShlokas = (): DailyShloka[] => {
  const saved = localStorage.getItem(SHLOKAS_KEY);
  if (!saved) {
    localStorage.setItem(SHLOKAS_KEY, JSON.stringify(DEFAULT_SHLOKAS));
    return DEFAULT_SHLOKAS;
  }
  return JSON.parse(saved);
};

export const saveShloka = (shloka: DailyShloka): void => {
  const shlokas = getShlokas();
  const existingIndex = shlokas.findIndex(s => s.id === shloka.id);
  if (existingIndex >= 0) {
    shlokas[existingIndex] = shloka;
  } else {
    shlokas.push(shloka);
  }
  localStorage.setItem(SHLOKAS_KEY, JSON.stringify(shlokas));
};

export const deleteShloka = (id: string): void => {
  const shlokas = getShlokas();
  const filtered = shlokas.filter(s => s.id !== id);
  localStorage.setItem(SHLOKAS_KEY, JSON.stringify(filtered));
};

export const importShlokas = (newShlokas: DailyShloka[], replace: boolean = false): void => {
  if (replace) {
    localStorage.setItem(SHLOKAS_KEY, JSON.stringify(newShlokas));
  } else {
    const existing = getShlokas();
    const merged = [...existing];
    newShlokas.forEach(newShloka => {
      const existingIndex = merged.findIndex(s => s.id === newShloka.id);
      if (existingIndex >= 0) {
        merged[existingIndex] = newShloka;
      } else {
        merged.push(newShloka);
      }
    });
    localStorage.setItem(SHLOKAS_KEY, JSON.stringify(merged));
  }
};

export const getShlokaOfTheDay = (): DailyShloka | null => {
  const shlokas = getShlokas().filter(s => s.active);
  if (shlokas.length === 0) return null;
  
  // Use date-based selection to show same shloka throughout the day
  const today = new Date();
  const dayOfYear = Math.floor((today.getTime() - new Date(today.getFullYear(), 0, 0).getTime()) / 86400000);
  const index = dayOfYear % shlokas.length;
  return shlokas[index];
};

export const resetShlokas = (): void => {
  localStorage.setItem(SHLOKAS_KEY, JSON.stringify(DEFAULT_SHLOKAS));
};

// Sidebar Menu Order Management
const SIDEBAR_MENU_KEY = 'ram_naam_sidebar_menu';

export const getSidebarMenu = (): SidebarMenuItem[] => {
  const stored = localStorage.getItem(SIDEBAR_MENU_KEY);
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      // Merge with defaults to handle any new items added
      const storedIds = parsed.map((item: SidebarMenuItem) => item.id);
      const newItems = DEFAULT_SIDEBAR_MENU.filter(item => !storedIds.includes(item.id));
      // Update isSpecial from defaults to ensure consistency
      const merged = parsed.map((item: SidebarMenuItem) => {
        const defaultItem = DEFAULT_SIDEBAR_MENU.find(d => d.id === item.id);
        return defaultItem ? { ...item, isSpecial: defaultItem.isSpecial } : item;
      });
      return [...merged, ...newItems].sort((a: SidebarMenuItem, b: SidebarMenuItem) => a.order - b.order);
    } catch (e) {
      return [...DEFAULT_SIDEBAR_MENU];
    }
  }
  return [...DEFAULT_SIDEBAR_MENU];
};

export const saveSidebarMenu = (menu: SidebarMenuItem[]): void => {
  localStorage.setItem(SIDEBAR_MENU_KEY, JSON.stringify(menu));
};

export const moveSidebarItem = (itemId: string, direction: 'up' | 'down'): SidebarMenuItem[] => {
  const menu = getSidebarMenu();
  const index = menu.findIndex(item => item.id === itemId);
  
  if (index === -1) return menu;
  
  const newIndex = direction === 'up' ? index - 1 : index + 1;
  if (newIndex < 0 || newIndex >= menu.length) return menu;
  
  // Swap the items
  const newMenu = [...menu];
  [newMenu[index], newMenu[newIndex]] = [newMenu[newIndex], newMenu[index]];
  
  // Update order values
  newMenu.forEach((item, i) => item.order = i);
  
  saveSidebarMenu(newMenu);
  return newMenu;
};

export const toggleSidebarItemVisibility = (itemId: string): SidebarMenuItem[] => {
  const menu = getSidebarMenu();
  const item = menu.find(m => m.id === itemId);
  if (item) {
    item.visible = !item.visible;
    saveSidebarMenu(menu);
  }
  return menu;
};

export const resetSidebarMenu = (): SidebarMenuItem[] => {
  const defaultMenu = [...DEFAULT_SIDEBAR_MENU];
  saveSidebarMenu(defaultMenu);
  return defaultMenu;
};



// Ekadashi Reminder Functions
const EKADASHI_SETTINGS_KEY = 'ram_naam_ekadashi_settings';
const EKADASHI_LAST_SHOWN_KEY = 'ram_naam_ekadashi_last_shown';

export const getEkadashiSettings = (): EkadashiReminderSettings => {
  const stored = localStorage.getItem(EKADASHI_SETTINGS_KEY);
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      // Migrate old settings format to new format
      if (!parsed.reminderType) {
        return {
          enabled: parsed.enabled ?? true,
          reminderType: parsed.reminderDaysBefore === 1 ? 'day_before' : 'morning_of',
          reminderDaysBefore: parsed.reminderDaysBefore ?? 1,
          reminderTime: parsed.reminderTime ?? '06:00',
          notificationsEnabled: false
        };
      }
      return parsed;
    } catch (e) {
      // Return defaults
    }
  }
  return {
    enabled: true,
    reminderType: 'day_before',
    reminderDaysBefore: 1,
    reminderTime: '06:00',
    notificationsEnabled: false
  };
};

export const saveEkadashiSettings = (settings: EkadashiReminderSettings): void => {
  localStorage.setItem(EKADASHI_SETTINGS_KEY, JSON.stringify(settings));
};

export const getNextEkadashi = (): EkadashiDate | null => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  for (const ekadashi of EKADASHI_DATES) {
    // Use VAISHNAV date for determining the next ekadashi
    const ekadashiDate = parseLocalDate(getVaishnavDateStr(ekadashi));
    if (ekadashiDate >= today) {
      return ekadashi;
    }
  }
  return null;
};

// Helper to get the VAISHNAV date for an Ekadashi (prioritizes vaishnavDate over base date)
const getVaishnavDateStr = (ekadashi: EkadashiDate): string => {
  return ekadashi.vaishnavDate || ekadashi.date;
};

// Helper: Parse "YYYY-MM-DD" as LOCAL date (not UTC)
const parseLocalDate = (dateStr: string): Date => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
};

export const getUpcomingEkadashis = (count: number = 5): EkadashiDate[] => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Use VAISHNAV date for filtering upcoming ekadashis
  return EKADASHI_DATES
    .filter(e => parseLocalDate(getVaishnavDateStr(e)) >= today)
    .slice(0, count);
};

export const getDaysUntilEkadashi = (ekadashi: EkadashiDate): number => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // Use VAISHNAV date for calculating days until
  const ekadashiDate = parseLocalDate(getVaishnavDateStr(ekadashi));
  const diffTime = ekadashiDate.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

export const shouldShowEkadashiReminder = (): { show: boolean; ekadashi: EkadashiDate | null } => {
  const settings = getEkadashiSettings();
  if (!settings.enabled) {
    return { show: false, ekadashi: null };
  }

  const nextEkadashi = getNextEkadashi();
  if (!nextEkadashi) {
    return { show: false, ekadashi: null };
  }

  const daysUntil = getDaysUntilEkadashi(nextEkadashi);
  
  // Check if we should show based on settings
  if (daysUntil > settings.reminderDaysBefore) {
    return { show: false, ekadashi: null };
  }

  // Check if already shown today for this ekadashi
  const lastShown = localStorage.getItem(EKADASHI_LAST_SHOWN_KEY);
  const today = new Date().toISOString().split('T')[0];
  const lastShownKey = `${nextEkadashi.date}_${today}`;
  
  if (lastShown === lastShownKey) {
    return { show: false, ekadashi: null };
  }

  return { show: true, ekadashi: nextEkadashi };
};

export const markEkadashiReminderShown = (ekadashi: EkadashiDate): void => {
  const today = new Date().toISOString().split('T')[0];
  const key = `${ekadashi.date}_${today}`;
  localStorage.setItem(EKADASHI_LAST_SHOWN_KEY, key);
};

export const getAllEkadashis = (): EkadashiDate[] => {
  return EKADASHI_DATES;
};

export const getEkadashiByMonth = (year: number, month: number): EkadashiDate[] => {
  return EKADASHI_DATES.filter(e => {
    const date = parseLocalDate(e.date);
    return date.getFullYear() === year && date.getMonth() === month;
  });
};


// ============ BLOG FUNCTIONS ============

// Default blog categories
const DEFAULT_BLOG_CATEGORIES: BlogCategory[] = [
  { id: 'spiritual-wisdom', name: 'Spiritual Wisdom', slug: 'spiritual-wisdom', description: 'Divine teachings and spiritual insights', color: '#F97316' },
  { id: 'sant-vani', name: 'Sant Vani', slug: 'sant-vani', description: 'Teachings from saints and gurus', color: '#8B5CF6' },
  { id: 'festivals', name: 'Festivals', slug: 'festivals', description: 'Hindu festivals and celebrations', color: '#EC4899' },
  { id: 'stories', name: 'Divine Stories', slug: 'stories', description: 'Mythological and devotional stories', color: '#10B981' },
  { id: 'sadhana', name: 'Sadhana Tips', slug: 'sadhana', description: 'Guidance for spiritual practice', color: '#3B82F6' },
];

// Calculate reading time based on word count (~200 words per minute)
const calculateReadingTime = (content: string): number => {
  const text = content.replace(/<[^>]*>/g, ''); // Strip HTML
  const wordCount = text.split(/\s+/).filter(word => word.length > 0).length;
  return Math.max(1, Math.ceil(wordCount / 200));
};

// Generate slug from title
const generateSlug = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

// Get all blog categories
export const getBlogCategories = async (): Promise<BlogCategory[]> => {
  try {
    const categories = await getAllFromStore<BlogCategory>(BLOG_CATEGORIES_STORE);
    if (categories.length === 0) {
      // Initialize with default categories
      await saveMultipleToStore(BLOG_CATEGORIES_STORE, DEFAULT_BLOG_CATEGORIES);
      return DEFAULT_BLOG_CATEGORIES;
    }
    return categories;
  } catch {
    return DEFAULT_BLOG_CATEGORIES;
  }
};

// Save blog category
export const saveBlogCategory = async (category: BlogCategory): Promise<void> => {
  await saveToStore(BLOG_CATEGORIES_STORE, category);
};

// Delete blog category
export const deleteBlogCategory = async (id: string): Promise<void> => {
  await deleteFromStore(BLOG_CATEGORIES_STORE, id);
};

// Get all blog posts
export const getBlogPosts = async (): Promise<BlogPost[]> => {
  try {
    const posts = await getAllFromStore<BlogPost>(BLOGS_STORE);
    return posts.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  } catch {
    return [];
  }
};

// Get published blog posts only
export const getPublishedBlogPosts = async (): Promise<BlogPost[]> => {
  const posts = await getBlogPosts();
  return posts.filter(p => p.status === 'published');
};

// Get blog post by ID
export const getBlogPostById = async (id: string): Promise<BlogPost | null> => {
  try {
    return await getOneFromStore<BlogPost>(BLOGS_STORE, id);
  } catch {
    return null;
  }
};

// Get blog post by slug
export const getBlogPostBySlug = async (slug: string): Promise<BlogPost | null> => {
  const posts = await getBlogPosts();
  return posts.find(p => p.slug === slug) || null;
};

// Save blog post (create or update)
export const saveBlogPost = async (post: Partial<BlogPost>, authorId: string, authorName: string, authorAvatar?: string): Promise<BlogPost> => {
  const now = new Date().toISOString();
  const isNew = !post.id;
  
  const fullPost: BlogPost = {
    id: post.id || `blog-${Date.now()}`,
    title: post.title || 'Untitled',
    slug: post.slug || generateSlug(post.title || 'untitled'),
    excerpt: post.excerpt || '',
    content: post.content || '',
    featuredImage: post.featuredImage,
    author: {
      id: authorId,
      name: authorName,
      avatar: authorAvatar
    },
    category: post.category || 'spiritual-wisdom',
    tags: post.tags || [],
    status: post.status || 'draft',
    metaDescription: post.metaDescription,
    views: post.views || 0,
    likes: post.likes || [],
    bookmarkedBy: post.bookmarkedBy || [],
    comments: post.comments || [],
    readingTimeMinutes: calculateReadingTime(post.content || ''),
    createdAt: post.createdAt || now,
    updatedAt: now,
    publishedAt: post.status === 'published' && !post.publishedAt ? now : post.publishedAt,
  };
  
  await saveToStore(BLOGS_STORE, fullPost);
  return fullPost;
};

// Delete blog post
export const deleteBlogPost = async (id: string): Promise<void> => {
  await deleteFromStore(BLOGS_STORE, id);
};

// Increment blog post views
export const incrementBlogViews = async (postId: string): Promise<void> => {
  const post = await getBlogPostById(postId);
  if (post) {
    post.views += 1;
    await saveToStore(BLOGS_STORE, post);
  }
};

// Toggle like on blog post
export const toggleBlogLike = async (postId: string, userId: string): Promise<boolean> => {
  const post = await getBlogPostById(postId);
  if (!post) return false;
  
  const likeIndex = post.likes.indexOf(userId);
  if (likeIndex === -1) {
    post.likes.push(userId);
  } else {
    post.likes.splice(likeIndex, 1);
  }
  
  await saveToStore(BLOGS_STORE, post);
  return likeIndex === -1; // Returns true if now liked, false if unliked
};

// Toggle bookmark on blog post
export const toggleBlogBookmark = async (postId: string, userId: string): Promise<boolean> => {
  const post = await getBlogPostById(postId);
  if (!post) return false;
  
  const bookmarkIndex = post.bookmarkedBy.indexOf(userId);
  if (bookmarkIndex === -1) {
    post.bookmarkedBy.push(userId);
  } else {
    post.bookmarkedBy.splice(bookmarkIndex, 1);
  }
  
  await saveToStore(BLOGS_STORE, post);
  return bookmarkIndex === -1; // Returns true if now bookmarked, false if removed
};

// Add comment to blog post
export const addBlogComment = async (postId: string, userId: string, userName: string, content: string, userAvatar?: string): Promise<BlogComment | null> => {
  const post = await getBlogPostById(postId);
  if (!post) return null;
  
  const comment: BlogComment = {
    id: `comment-${Date.now()}`,
    postId,
    userId,
    userName,
    userAvatar,
    content,
    createdAt: new Date().toISOString(),
    likes: []
  };
  
  post.comments.push(comment);
  await saveToStore(BLOGS_STORE, post);
  return comment;
};

// Delete comment from blog post
export const deleteBlogComment = async (postId: string, commentId: string): Promise<void> => {
  const post = await getBlogPostById(postId);
  if (!post) return;
  
  post.comments = post.comments.filter(c => c.id !== commentId);
  await saveToStore(BLOGS_STORE, post);
};

// Toggle like on comment
export const toggleCommentLike = async (postId: string, commentId: string, userId: string): Promise<boolean> => {
  const post = await getBlogPostById(postId);
  if (!post) return false;
  
  const comment = post.comments.find(c => c.id === commentId);
  if (!comment) return false;
  
  const likeIndex = comment.likes.indexOf(userId);
  if (likeIndex === -1) {
    comment.likes.push(userId);
  } else {
    comment.likes.splice(likeIndex, 1);
  }
  
  await saveToStore(BLOGS_STORE, post);
  return likeIndex === -1;
};

// Get related posts (same category, excluding current)
export const getRelatedPosts = async (postId: string, category: string, limit: number = 3): Promise<BlogPost[]> => {
  const posts = await getPublishedBlogPosts();
  return posts
    .filter(p => p.id !== postId && p.category === category)
    .slice(0, limit);
};

// Search blog posts
export const searchBlogPosts = async (query: string): Promise<BlogPost[]> => {
  const posts = await getPublishedBlogPosts();
  const lowerQuery = query.toLowerCase();
  
  return posts.filter(p => 
    p.title.toLowerCase().includes(lowerQuery) ||
    p.excerpt.toLowerCase().includes(lowerQuery) ||
    p.content.toLowerCase().includes(lowerQuery) ||
    p.tags.some(tag => tag.toLowerCase().includes(lowerQuery))
  );
};

// Get blog analytics
export const getBlogAnalytics = async (): Promise<{
  totalPosts: number;
  publishedPosts: number;
  draftPosts: number;
  totalViews: number;
  totalLikes: number;
  totalComments: number;
  topPosts: BlogPost[];
}> => {
  const posts = await getBlogPosts();
  const published = posts.filter(p => p.status === 'published');
  
  return {
    totalPosts: posts.length,
    publishedPosts: published.length,
    draftPosts: posts.length - published.length,
    totalViews: posts.reduce((sum, p) => sum + p.views, 0),
    totalLikes: posts.reduce((sum, p) => sum + p.likes.length, 0),
    totalComments: posts.reduce((sum, p) => sum + p.comments.length, 0),
    topPosts: [...published].sort((a, b) => b.views - a.views).slice(0, 5)
  };
};
